import React, { useEffect, useMemo, useState } from "react";
import { FiX, FiExternalLink, FiGlobe, FiFileText } from "react-icons/fi";
import { HiLightBulb } from "react-icons/hi";

// Extract clean hostname/domain from URL
export function getDomain(url) {
  if (!url) return "";
  try {
    const cleanUrl = url.startsWith("http") ? url : `https://${url}`;
    const parsed = new URL(cleanUrl);
    return parsed.hostname.replace(/^www\./, "");
  } catch (e) {
    return "";
  }
}

// Google Favicon API
export function getFaviconUrl(domain) {
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
}

// Generate initials fallback from title or domain
export function getInitials(text) {
  if (!text) return "GR";
  const words = text.replace(/[^a-zA-Z0-9\s]/g, "").trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Individual Favicon Circle with graceful image error handling
export function FaviconCircle({ url, title, size = 20, className = "" }) {
  const [hasError, setHasError] = useState(false);
  const domain = useMemo(() => getDomain(url), [url]);
  const faviconUrl = useMemo(() => (domain ? getFaviconUrl(domain) : null), [domain]);
  const initials = useMemo(() => getInitials(domain || title), [domain, title]);

  if (!faviconUrl || hasError) {
    return (
      <span
        className={`source-avatar-fallback ${className}`}
        style={{ width: size, height: size, minWidth: size, minHeight: size }}
        title={domain || title}
      >
        {initials || <FiFileText size={Math.round(size * 0.6)} />}
      </span>
    );
  }

  return (
    <span
      className={`source-avatar-img-wrap ${className}`}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
      title={domain || title}
    >
      <img
        src={faviconUrl}
        alt={domain || "source"}
        className="source-avatar-img"
        referrerPolicy="no-referrer"
        onError={() => setHasError(true)}
      />
    </span>
  );
}

// Source Pill Button (Image 2)
export function SourcePillButton({ sources = [], isActive = false, onClick }) {
  if (!sources || sources.length === 0) return null;

  // Up to 3 preview icons for the overlapping stack
  const previewSources = sources.slice(0, 3);

  return (
    <button
      type="button"
      className={`msg-source-pill-btn ${isActive ? "active" : ""}`}
      onClick={onClick}
      aria-label={`${sources.length} sources`}
      title={`Click to view ${sources.length} sources in split view`}
    >
      <div className="source-pill-avatars">
        {previewSources.map((src, i) => {
          const targetUrl = src.pdf_url || src.url || src.link || null;
          return (
            <FaviconCircle
              key={i}
              url={targetUrl}
              title={src.title}
              size={18}
              className="source-pill-avatar"
            />
          );
        })}
      </div>
      <span className="source-pill-text">
        {sources.length} {sources.length === 1 ? "source" : "sources"}
      </span>
    </button>
  );
}

// Helper to check if source is a GR PDF document (strictly from Vector DB or explicitly marked as GR)
function isPdfSource(src) {
  if (
    src.category === "Government GR" ||
    src.category === "Government GR Document" ||
    src.category === "Government GR PDF" ||
    src.is_gr === true
  ) {
    return true;
  }
  
  // If it's just a web search result that happens to be a PDF, we might not want to classify it as an official GR unless it's explicitly from the DB
  const targetUrl = src.pdf_url || src.url || src.link || src.file_name || "";
  if (!targetUrl) return false;
  
  // We only rely on category for Vector DB hits now to avoid false positives from Web Search
  return false;
}

// Helper to check if source is an official government website/portal (non-PDF)
function isOfficialGovtWebsite(src) {
  if (isPdfSource(src)) return false;
  if (src.category === "Official Govt Website") return true;
  const targetUrl = src.pdf_url || src.url || src.link || "";
  if (!targetUrl) return false;
  const domain = getDomain(targetUrl);
  return (
    domain.includes("gov.in") ||
    domain.includes("nic.in") ||
    domain.includes("maharashtra.gov.in")
  );
}

// Helper to check if source is an official government source (legacy fallback)
function isOfficialGovtSource(src) {
  if (src.is_official === true || src.category === "Government GR" || src.category === "Government GR Document" || src.category === "Government GR PDF" || src.category === "Official Govt Website" || src.category === "Official Govt PDF (Web)") return true;
  const targetUrl = src.pdf_url || src.url || src.link || "";
  if (!targetUrl) return false;
  const domain = getDomain(targetUrl);
  return (
    domain.includes("gov.in") ||
    domain.includes("nic.in") ||
    targetUrl.includes("archive.org/details/in.gov.maharashtra.gr") ||
    targetUrl.includes("github.com/orgpedia/mahgrs")
  );
}

// Right-Side Split View Sidebar Panel (Image 3)
export default function SourcesSidebar({
  isOpen,
  onClose,
  sources = [],
  query = "",
  language = "en",
}) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const { grPdfSources, officialWebSources, generalWebSources } = useMemo(() => {
    const pdfs = [];
    const webOfficial = [];
    const general = [];

    sources.forEach((src) => {
      if (isPdfSource(src)) {
        pdfs.push(src);
      } else if (isOfficialGovtWebsite(src) || isOfficialGovtSource(src)) {
        webOfficial.push(src);
      } else {
        general.push(src);
      }
    });

    return {
      grPdfSources: pdfs,
      officialWebSources: webOfficial,
      generalWebSources: general,
    };
  }, [sources]);

  if (!isOpen) return null;

  const renderCard = (src, idx, sourceType) => {
    const targetUrl = src.pdf_url || src.url || src.link || null;
    const domain = getDomain(targetUrl);

    let badgeText = null;
    let badgeStyle = {};
    let cardStyle = {};

    if (sourceType === "gr_pdf") {
      badgeText = "Govt GR PDF";
      badgeStyle = { color: "#b45309", background: "#fef3c7" };
      cardStyle = { borderColor: "rgba(217, 119, 6, 0.3)", background: "rgba(251, 191, 36, 0.03)" };
    } else if (sourceType === "official_website") {
      badgeText = "Govt Website";
      badgeStyle = { color: "#0369a1", background: "#e0f2fe" };
      cardStyle = { borderColor: "rgba(2, 132, 199, 0.25)", background: "rgba(224, 242, 254, 0.2)" };
    } else {
      badgeText = "Web Reference";
      badgeStyle = { color: "#475569", background: "#f1f5f9" };
      cardStyle = {};
    }

    return (
      <a
        key={idx}
        href={targetUrl || "#"}
        target={targetUrl ? "_blank" : "_self"}
        rel="noopener noreferrer"
        className="source-card-item"
        title={src.title}
        style={cardStyle}
      >
        <div className="source-card-icon-col">
          <FaviconCircle
            url={targetUrl}
            title={src.title}
            size={28}
            className="source-card-favicon"
          />
        </div>

        <div className="source-card-main-col">
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <h4 className="source-card-title" style={{ flex: 1 }}>{src.title}</h4>
            {badgeText && (
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  padding: "1px 6px",
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  ...badgeStyle,
                }}
              >
                {badgeText}
              </span>
            )}
          </div>
          <div className="source-card-meta">
            {domain && <span className="source-card-domain">{domain}</span>}
            {src.department && (
              <span className="source-card-dept" title={src.department}>
                {src.department}
              </span>
            )}
            {src.date && <span className="source-card-date">{src.date}</span>}
          </div>
        </div>

        {targetUrl && (
          <div className="source-card-action-col">
            <span className="source-card-external-btn" aria-label="Open source">
              <FiExternalLink size={14} />
            </span>
          </div>
        )}
      </a>
    );
  };

  return (
    <>
      {/* Mobile / Tablet overlay backdrop */}
      <div
        className="sources-sidebar-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className="sources-sidebar-panel" aria-label="Sources split view">
        {/* Header with Title and Close Button */}
        <div className="sources-sidebar-header">
          <div className="sources-sidebar-header-left">
            <h3 className="sources-sidebar-title">Sources</h3>
            <span className="sources-count-badge">{sources.length}</span>
          </div>
          <button
            type="button"
            className="sources-sidebar-close-btn"
            onClick={onClose}
            aria-label="Close sources panel"
            title="Close sources panel"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="sources-sidebar-body">
          {/* Research & Thinking Breakdown (Grok-exact style) */}
          <div className="sources-thinking-section">
            <div className="sources-thinking-header">
              <HiLightBulb className="sources-bulb-icon" />
              <span>Thinking about your request</span>
            </div>

            {query ? (
              <div className="sources-search-query-card">
                <div className="sources-query-left">
                  <FiGlobe className="sources-globe-icon" />
                  <div className="sources-query-content">
                    <span className="sources-query-sublabel">Searched documents & web</span>
                    <span className="sources-query-text">{query}</span>
                  </div>
                </div>
                <span className="sources-query-count">{sources.length}</span>
              </div>
            ) : (
              <div className="sources-search-query-card">
                <div className="sources-query-left">
                  <FiGlobe className="sources-globe-icon" />
                  <span className="sources-query-sublabel">Consulted reference sources</span>
                </div>
                <span className="sources-query-count">{sources.length}</span>
              </div>
            )}
          </div>

          {/* Sources List Section */}
          <div className="sources-list-section">
            {grPdfSources.length > 0 && (
              <div className="sources-group-block" style={{ marginBottom: (officialWebSources.length > 0 || generalWebSources.length > 0) ? "18px" : "0" }}>
                <div className="sources-section-label" style={{ color: "#d97706", marginBottom: "8px", fontWeight: "700" }}>
                  <span>📄 Official Govt GR Documents ({grPdfSources.length})</span>
                </div>
                <div className="sources-cards-list">
                  {grPdfSources.map((src, idx) => renderCard(src, idx, "gr_pdf"))}
                </div>
              </div>
            )}

            {officialWebSources.length > 0 && (
              <div className="sources-group-block" style={{ marginBottom: generalWebSources.length > 0 ? "18px" : "0" }}>
                <div className="sources-section-label" style={{ color: "#0284c7", marginBottom: "8px", fontWeight: "700" }}>
                  <span>🏛️ Official Govt Websites & Portals ({officialWebSources.length})</span>
                </div>
                <div className="sources-cards-list">
                  {officialWebSources.map((src, idx) => renderCard(src, idx, "official_website"))}
                </div>
              </div>
            )}

            {generalWebSources.length > 0 && (
              <div className="sources-group-block">
                <div className="sources-section-label" style={{ color: "#64748b", marginBottom: "8px", fontWeight: "700" }}>
                  <span>🌐 Additional Web References ({generalWebSources.length})</span>
                </div>
                <div className="sources-cards-list">
                  {generalWebSources.map((src, idx) => renderCard(src, idx, "general_web"))}
                </div>
              </div>
            )}

            {sources.length === 0 && (
              <div className="sources-section-label">
                <span>No sources available</span>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

